import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Add, Link } from "iconsax-reactjs"
import { Loader2, RefreshCcwIcon } from "lucide-react"
import { useMemo, useState } from "react"
import { toast } from "sonner"

import { getTikTokTrendingInfo, updateDataGmvCreator } from "@/api"
import { DisplayInfo, IconButton, MetricRow } from "@/components/shared"
import { Button, Card, CardContent, CardHeader } from "@/components/ui"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { ConnectSocialModal } from "@/features/jobs/explore/components/campaign"
import { TikTokAnalytics } from "@/features/users/components/profile/tiktok-analytics"
import { EFolderAssets } from "@/modules/helpers/enum"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import {
  formatNumber,
  getIdFromLinkTiktok,
  getImageByNameFromS3,
  openTikTokShowcaseAuth
} from "@/modules/helpers/utils"

// Constants
const DEFAULT_DATE_RANGE_DAYS = 30
const TIKTOK_TRENDING_PAGE_SIZE = 100
const AUTH_REVALIDATE_DELAY_MS = 6000

// Types
interface DateRange {
  startTime: string
  endTime: string
}

// Helper functions
const getDefaultDateRange = (): DateRange => ({
  startTime: new Date(Date.now() - DEFAULT_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0],
  endTime: new Date().toISOString().split("T")[0]
})

// Sub-components
interface ConnectChannelHeaderProps {
  onConnectClick: () => void
}

function ConnectChannelHeader({ onConnectClick }: ConnectChannelHeaderProps) {
  return (
    <div className="px-2 flex items-center gap-2 justify-between mb-4">
      <div className="px-2 flex items-center gap-2">
        <p className="text-sm font-medium">Kết nối kênh</p>
        <Link size={16} />
      </div>
      <IconButton onClick={onConnectClick}>
        <Add size={16} />
      </IconButton>
    </div>
  )
}

interface ProfileHeaderProps {
  avatarUrl?: string
  username?: string
  fullName?: string
  tiktokLink?: string
  isAuthorized?: boolean
  onAuthorize: () => void
}

function ProfileHeader({
  avatarUrl,
  username,
  tiktokLink,
  isAuthorized,
  onAuthorize
}: ProfileHeaderProps) {
  const displayName = username || "_ _"
  const tiktokId = tiktokLink ? getIdFromLinkTiktok(tiktokLink) : "_ _"

  return (
    <CardHeader className="flex justify-between w-full gap-4 py-0">
      <div className="flex items-center gap-4">
        <Avatar className="w-12 h-12 border border-gray-200">
          <AvatarImage src={avatarUrl || ""} alt={username || ""} />
          <AvatarFallback>{username?.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div>
          <h3 className="text-base font-bold text-slate-900">{displayName}</h3>
          <div className="text-xs text-slate-500 mt-0.5">{tiktokId}</div>
        </div>
      </div>

      <div className="ml-auto flex flex-col items-end gap-1.5" data-tour="authorize-showcase">
        <Button
          size="sm"
          disabled={isAuthorized}
          onClick={onAuthorize}
          className={`h-6 px-4 !text-xs font-normal bg-white text-primary rounded-full shadow-none ${
            isAuthorized ? "opacity-60" : ""
          }`}
        >
          {isAuthorized ? "Đã ủy quyền" : "Ủy quyền trưng bày"}
        </Button>
      </div>
    </CardHeader>
  )
}

interface OverviewSectionProps {
  followerCount?: number
  totalGmv?: number
  followingCount?: number
  likesCount?: number
  onRefreshGmv?: () => void
  isRefreshingGmv?: boolean
}

function OverviewSection({
  followerCount,
  totalGmv,
  followingCount,
  likesCount,
  onRefreshGmv,
  isRefreshingGmv
}: OverviewSectionProps) {
  return (
    <section className="rounded-xl p-4 bg-white">
      <div className="mb-3">
        <span className="inline-block px-3 py-1 text-xs font-normal bg-background text-primary rounded-full">
          Tổng quan
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <DisplayInfo title="Người theo dõi" value={formatNumber(followerCount)} />
        <div className="flex flex-col items-center justify-center p-3 rounded-md text-center bg-white">
          <div className="text-base font-bold text-slate-900 mb-1">{formatNumber(totalGmv)}</div>
          <div className="flex items-center gap-1 justify-center">
            <div className="text-xs font-medium text-slate-600">Tổng GMV</div>
            {onRefreshGmv && (
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 rounded-full text-primary-800"
                onClick={onRefreshGmv}
                disabled={isRefreshingGmv}
              >
                {isRefreshingGmv ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCcwIcon size={12} />
                )}
              </Button>
            )}
          </div>
        </div>
        <DisplayInfo title="Đang theo dõi" value={formatNumber(followingCount)} />
        <DisplayInfo title="Lượt thích" value={formatNumber(likesCount)} />
      </div>
    </section>
  )
}

interface ChannelAnalysisSectionProps {
  averageEngagementRate?: number
  postingRate?: number
  averageShareCount?: number
  averageViewCount?: number
  averageLikeCount?: number
}

function ChannelAnalysisSection({
  averageEngagementRate,
  postingRate,
  averageShareCount,
  averageViewCount,
  averageLikeCount
}: ChannelAnalysisSectionProps) {
  const engagementRateDisplay = averageEngagementRate ? `${averageEngagementRate}%` : "_ _"
  const postingRateDisplay = postingRate ? `${postingRate}%` : "_ _"

  return (
    <section className="rounded-xl p-4 bg-white">
      <div className="mb-3">
        <span className="inline-block px-3 py-1 text-xs font-normal bg-background text-primary rounded-full">
          Phân tích kênh
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <DisplayInfo title="Tỷ lệ tương tác" value={engagementRateDisplay} />
        <DisplayInfo
          title="Tỷ lệ đăng bài"
          tooltip="Để xem được tỷ lệ này, KOC cần uỷ quyền trang trưng bày (Showcase) cho ZZP"
          value={postingRateDisplay}
        />
      </div>

      <div className="border-t border-slate-300 pt-3 mt-3">
        <MetricRow label="Lượt chia sẻ trung bình" value={formatNumber(averageShareCount)} />
        <MetricRow label="Lượt xem trung bình" value={formatNumber(averageViewCount)} />
        <MetricRow label="Lượt thích trung bình" value={formatNumber(averageLikeCount)} />
      </div>
    </section>
  )
}

// Main component
export function SocialProfileSection({ isProfile }: { isProfile: boolean }) {
  const queryClient = useQueryClient()
  const { user, setUser } = useCurrentUser()

  const [visible, setVisible] = useState(false)
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange)

  // Derived state
  const tikTokInfo = useMemo(() => user?.tiktoks, [user])

  // Queries
  const { data: trendingData, isLoading: isLoadingTrending } = useQuery({
    queryKey: ["tiktok_trending_info", dateRange.startTime, dateRange.endTime],
    queryFn: () =>
      getTikTokTrendingInfo({
        order: "ASC",
        page: 1,
        take: TIKTOK_TRENDING_PAGE_SIZE,
        sortBy: "createdAt",
        startTime: dateRange.startTime,
        endTime: dateRange.endTime
      })
  })

  const trendingDataPoints = useMemo(() => trendingData?.data?.data || [], [trendingData])

  const { mutate: refreshGmv, isPending: isRefreshingGmv } = useMutation({
    mutationKey: ["updateDataGmvCreator", user?.id],
    mutationFn: () => {
      if (!user?.id) {
        throw new Error("USER_ID_NOT_FOUND")
      }
      return updateDataGmvCreator(user.id)
    },
    onSuccess: (response) => {
      const gmvData = response?.data?.data

      if (user?.tiktoks && gmvData) {
        setUser({
          ...user,
          tiktoks: {
            ...user.tiktoks,
            totalGmv: gmvData.totalGmv,
            liveGmv: gmvData.liveGmv,
            videoGmv: gmvData.videoGmv
          }
        })
      }

      queryClient.invalidateQueries({ queryKey: ["user_info"] })
      toast.success("Đã cập nhật GMV")
    },
    onError: () => {
      toast.error("Không thể cập nhật GMV. Vui lòng thử lại")
    }
  })

  // Handlers
  const handleTimeFilterChange = (startTime: string, endTime: string) => {
    queryClient.invalidateQueries({ queryKey: ["tiktok_trending_info"] })
    setDateRange({ startTime, endTime })
  }

  const handleAuthorize = () => {
    if (user?.isAuthCreatorTap || !user?.id) return

    openTikTokShowcaseAuth(user.id)

    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ["user_info"] })
    }, AUTH_REVALIDATE_DELAY_MS)
  }

  const handleOpenConnectModal = () => setVisible(true)
  const handleCloseConnectModal = () => setVisible(false)

  // Render
  return (
    <>
      {/* Connect channel prompt (shown when no TikTok linked) */}
      {isProfile && !tikTokInfo && <ConnectChannelHeader onConnectClick={handleOpenConnectModal} />}

      {/* TikTok profile section */}
      {tikTokInfo && (
        <>
          <Card className="w-full border-none shadow-none bg-background">
            <ProfileHeader
              avatarUrl={getImageByNameFromS3(user?.avatarName, user?.id, EFolderAssets.AVATAR)}
              username={user?.username}
              fullName={user?.profiles?.fullName}
              tiktokLink={tikTokInfo?.link}
              isAuthorized={user?.isAuthCreatorTap}
              onAuthorize={handleAuthorize}
            />
            <CardContent className="px-4 pt-0">
              <div className="space-y-2">
                <OverviewSection
                  followerCount={
                    tikTokInfo?.followerCount ? Number(tikTokInfo.followerCount) : undefined
                  }
                  totalGmv={tikTokInfo?.totalGmv}
                  followingCount={
                    tikTokInfo?.followingCount ? Number(tikTokInfo.followingCount) : undefined
                  }
                  likesCount={tikTokInfo?.likesCount ? Number(tikTokInfo.likesCount) : undefined}
                  onRefreshGmv={() => refreshGmv()}
                  isRefreshingGmv={isRefreshingGmv}
                />

                <ChannelAnalysisSection
                  averageEngagementRate={
                    tikTokInfo?.averageEngagementRate
                      ? Number(tikTokInfo.averageEngagementRate)
                      : undefined
                  }
                  postingRate={tikTokInfo?.postingRate}
                  averageShareCount={
                    tikTokInfo?.averageShareCount ? Number(tikTokInfo.averageShareCount) : undefined
                  }
                  averageViewCount={
                    tikTokInfo?.averageViewCount ? Number(tikTokInfo.averageViewCount) : undefined
                  }
                  averageLikeCount={
                    tikTokInfo?.averageLikeCount ? Number(tikTokInfo.averageLikeCount) : undefined
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card className="w-full border-none shadow-none mt-4 bg-background">
            <CardContent className="px-4 pt-0">
              <TikTokAnalytics
                trendingData={trendingDataPoints}
                followerGender={tikTokInfo?.followerGenders}
                followerStateLocation={tikTokInfo?.followerStateLocation}
                followerAges={tikTokInfo?.followerAges}
                isLoading={isLoadingTrending}
                onTimeFilterChange={handleTimeFilterChange}
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* Connect social modal */}
      <ConnectSocialModal
        isConnected={!tikTokInfo}
        visible={visible}
        onClose={handleCloseConnectModal}
      />
    </>
  )
}
